/**
 * Placeholder rendered under /apps/:appSlug/{strategies,discovery,...} and
 * any other child route that hasn't shipped yet. Instead of a mystery
 * "App workspace" panel, surface *what* this tab is meant to be so the
 * user knows whether to come back or keep moving.
 */
import { useLocation, useParams } from 'react-router-dom';

const ROUTE_COPY: Record<string, { title: string; lede: string; eta: string }> = {
  'trading-agent/strategies': {
    title: 'Strategies',
    lede: "Author, backtest, and pause trading strategies from one place. We're prototyping the editor — the tab is parked here so it stays wired to the URL.",
    eta: 'Coming soon',
  },
  'trading-agent/discovery': {
    title: 'Discovery',
    lede: 'Scan watchlists, Reddit sentiment, and option flow for fresh ideas. Backend is still plumbing data sources.',
    eta: 'Coming soon',
  },
};

function keyFor(pathname: string): string | null {
  const match = pathname.match(/\/apps\/([^/]+)\/([^/]+)/);
  return match ? `${match[1]}/${match[2]}` : null;
}

export default function AppPlaceholder() {
  const { appSlug } = useParams<{ appSlug: string }>();
  const { pathname } = useLocation();
  // `copy` is undefined when the path doesn't match any key in ROUTE_COPY —
  // every field access below must use optional chaining or this crashes
  // with "Cannot read properties of undefined" before render.
  const copy = ROUTE_COPY[keyFor(pathname) ?? ''];

  return (
    <section
      className="mx-auto flex min-h-[50vh] max-w-2xl flex-col items-start justify-center gap-5 px-4 py-16"
      style={{ color: 'var(--fg)' }}
    >
      <span
        className="inline-flex items-center gap-2"
        style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 10.5,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--fg-muted)',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent)',
          }}
        />
        {copy?.eta ?? 'Workspace'}
      </span>
      <h1
        style={{
          fontFamily: 'var(--font-display, Georgia, serif)',
          fontStyle: 'italic',
          fontSize: 'clamp(38px, 5vw, 60px)',
          fontWeight: 400,
          letterSpacing: '-0.02em',
          lineHeight: 0.95,
          margin: 0,
        }}
      >
        {copy?.title ?? appSlug ?? 'App workspace'}
      </h1>
      <p
        style={{
          fontFamily: 'var(--font-body, sans-serif)',
          fontSize: 15.5,
          lineHeight: 1.6,
          color: 'var(--fg-muted)',
          margin: 0,
          maxWidth: 560,
        }}
      >
        {copy?.lede ?? 'This surface belongs to a feature that is not yet live.'}
      </p>
    </section>
  );
}
